import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { FitMaskLookup } from 'src/app/compliance/shared/fit-mask/fit-mask-lookup';
import { ShippingRequestCandidate, ShippingStatusUpdate } from 'src/app/compliance/shipping-requests/models';
import { ShippingRequest } from 'src/app/compliance/shipping-requests/shipping-request';
import { ShippingRequestGridItem } from 'src/app/compliance/shipping-requests/shipping-request-grid-item';
import { UploadAttachment } from 'src/app/shared/components/upload-attachments/upload-attachment.model';
import { FetchArgs, PagingToken, PagingTokenFixed } from 'src/app/shared/models/core';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { KeyValuePair } from 'src/app/shared/models/key-value-pair.model';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { ShippingRequestsService } from '../../shipping-requests/shipping-requests.service';
import { LookupClient } from '../lookup-client.service';
import { ComplianceDocumentFilter } from '../models/compliance-document-filter.model';
import { FetchArguments2OfShippingRequestsSearchFilter } from '../models/fetch-arguments2of-shipping-requests-search-filter.model';
import { FitMaskLookupDto } from '../models/fit-mask-lookup-dto.model';
import { KeyValueDto } from '../models/key-value-dto.model';
import { KeyValuePairOfIntegerAndString } from '../models/key-value-pair-of-integer-and-string.model';
import { PagingTokenOfComplianceDocumentDto } from '../models/paging-token-of-compliance-document-dto.model';
import { PagingTokenOfShippingRequestDto } from '../models/paging-token-of-shipping-request-dto.model';
import { RequestBulkStatusUpdateDto } from '../models/request-bulk-status-update-dto.model';
import { ShippingRequestAddUpdateDto } from '../models/shipping-request-add-update-dto.model';
import { ShippingRequestCandidateDto } from '../models/shipping-request-candidate-dto.model';
import { ShippingRequestDetailsDto } from '../models/shipping-request-details-dto.model';
import { ShippingRequestsClient } from '../shipping-requests-client.service';
import { gridDate } from './grid-date.function';
import { NSwagMigrationService } from './nswag-migration.service';

// 15 endpoints
@Injectable({ providedIn: 'root' })
export class ShippingRequestsNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'ShippingRequestsNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationShippingRequestsService;
    private readonly legacy = inject(ShippingRequestsService);
    private readonly shippingRequestsClient = inject(ShippingRequestsClient);
    private readonly lookupClient = inject(LookupClient);

    // ** ShippingRequestsController.Search
    searchShippingRequests(args: any | FetchArguments2OfShippingRequestsSearchFilter): Observable<PagingToken<ShippingRequestGridItem[]> | PagingTokenOfShippingRequestDto> {
        return this.invoke(x => x
            ? this.legacy.searchShippingRequests(args)
            : this.shippingRequestsClient.search(args)
                .pipe(map((pipedResult) => this.mapPackageContents(pipedResult))));
    }

    // ** ShippingRequestsController.BulkStatusUpdate
    updateShippingStatus(shippingRequest: ShippingStatusUpdate | RequestBulkStatusUpdateDto): Observable<any | void> {
        return this.invoke(x => x
            ? this.legacy.updateShippingStatus(shippingRequest)
            : this.shippingRequestsClient.bulkStatusUpdate(shippingRequest));
    }

    // ** ShippingRequestsController.GetDetails
    getShippingRequest(id: number): Observable<ShippingRequest | ShippingRequestDetailsDto> {
        return this.invoke(x => x
            ? this.legacy.getShippingRequest(id)
            : this.shippingRequestsClient.getDetails(id));
    }

    // ** ShippingRequestsController.Add
    addShippingRequest(shippingRequest: ShippingRequest | ShippingRequestAddUpdateDto): Observable<ShippingRequest | ShippingRequestDetailsDto> {
        return this.invoke(x => x
            ? this.legacy.addShippingRequest(shippingRequest as ShippingRequest)
            : this.shippingRequestsClient.add(shippingRequest as ShippingRequestAddUpdateDto));
    }

    // ** ShippingRequestsController.Update
    updateShippingRequest(id: number, shippingRequest: ShippingRequest | ShippingRequestAddUpdateDto): Observable<ShippingRequest | ShippingRequestDetailsDto> {
        return this.invoke(x => x
            ? this.legacy.updateShippingRequest(id, shippingRequest as ShippingRequest)
            : this.shippingRequestsClient.update(id, shippingRequest as ShippingRequestAddUpdateDto));
    }

    // ** LookupController.GetPacketRequestFitMaskLookups
    getPacketRequestFitMasksLookups(): Observable<FitMaskLookup[] | FitMaskLookupDto[]> {
        return this.invoke(x => x
            ? this.legacy.getPacketRequestFitMasksLookups()
            : this.lookupClient.getPacketRequestFitMaskLookups());
    }

    // ** LookupController.GetPacketRequestMiscellaneousItemsLookups
    getPacketRequestMiscellaneousItemsLookups(): Observable<Lookup[] | KeyValueDto[]> {
        return this.invoke(x => x
            ? this.legacy.getPacketRequestMiscellaneousItemsLookups()
            : this.lookupClient.getPacketRequestMiscellaneousItemsLookups());
    }

    // ** ShippingRequestsController.SearchCandidates
    getCandidates(searchText: string): Observable<ShippingRequestCandidate[] | ShippingRequestCandidateDto[]> {
        return this.invoke(x => x
            ? this.legacy.getCandidates(searchText)
            : this.shippingRequestsClient.searchCandidates(searchText));
    }

    // ** ShippingRequestsController.GetAssignees
    getAssignees(): Observable<Lookup[] | KeyValueDto[]> {
        return this.invoke(x => x
            ? this.legacy.getAssignees()
            : this.shippingRequestsClient.getAssignees());
    }

    // ** ShippingRequestsController.GetDocuments
    getShippingRequestAttachments(shippingId, args: FetchArgs): Observable<PagingTokenFixed<UploadAttachment[]> | PagingTokenOfComplianceDocumentDto> {
        // Filter is ignored by legacy code, so I will ignore too.
        const filter: ComplianceDocumentFilter = {} as any;

        return this.invoke(x => x
            ? this.legacy.getShippingRequestAttachments(shippingId, args)
            : this.shippingRequestsClient.getDocuments(
                shippingId, // shippingRequestId
                filter.userId, // filter_UserId
                filter.name, // filter_Name
                filter.path, // filter_Path
                gridDate(filter.uploadedDate), // filter_UploadedDate
                filter.uploadedBy, // filter_UploadedBy
                filter.title, // filter_Title
                filter.shippingRequestId, // filter_ShippingRequestId
                filter.healthRequestId, // filter_HealthRequestId
                args.pageSize, // pageSize
                args.skip, // skip
                args.sort?.field, // sort_Field
                args.sort?.dir as any, // sort_Type
                undefined // paginated
            ));
    }

    // ** ShippingRequestsController.UploadDocuments
    uploadShippingRequestAttachments(shippingId: number, formData: FormData): Observable<unknown | number> {
        const file = formData.get('file') as File;
        return this.invoke(x => x
            ? this.legacy.uploadShippingRequestAttachments(shippingId, formData)
            : this.shippingRequestsClient.uploadDocuments(
                shippingId as any, // id: number,
                shippingId as any, // shippingRequestId: number | null | undefined,
                formData.get('healthRequestId') as any, // healthRequestId: number | null | undefined,
                formData.get('vendorId') as any, // vendorId: number | null | undefined,
                formData.get('requestId') ?? 0 as any, // requestId: number | undefined,
                formData.get('title') as any, // title: string | null | undefined,
                { data: file, fileName: file.name }, // file: FileParameter | null | undefined,
                formData.get('originalFileName') as any, // originalFileName: string | null | undefined,
                formData.get('recipeForSuccess') ?? false as any, // recipeForSuccess: boolean | undefined,
                formData.get('path') as any, // path: string | null | undefined
            ));
    }

    // ** ShippingRequestsController.RemoveDocument
    removeShippingRequestAttachments(shippingId: number, documentId: number): Observable<unknown | number> {
        return this.invoke(x => x
            ? this.legacy.removeShippingRequestAttachments(shippingId, documentId)
            : this.shippingRequestsClient.removeDocument(shippingId, documentId));
    }

    // ** ShippingRequestsController.DownloadFile
    getShippingRequestFile(shippingId: number, documentId: number): Observable<{ file: Blob; url: string }> {
        return this.invoke(x => x
            ? this.legacy.getShippingRequestFile(shippingId, documentId)
            : this.shippingRequestsClient.downloadFile(shippingId, documentId)
                .pipe(map(x => ({ file: x.data, url: URL.createObjectURL(x.data) }))));
    }

    // ** ShippingRequestsController.ReassignTickets
    reassignBulkTickets(assigneeId: number, ticketIds: number[]): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.reassignBulkTickets(assigneeId, ticketIds)
            : this.shippingRequestsClient.reassignTickets(assigneeId, ticketIds));
    }

    // ** ShippingRequestsController.GetChainOfCustodyLookupAsync
    getChainOfCustodyLookups(): Observable<Array<KeyValuePair<number, string>> | KeyValuePairOfIntegerAndString[]> {
        return this.invoke(x => x
            ? this.legacy.getChainOfCustodyLookups()
            : this.shippingRequestsClient.getChainOfCustodyLookup());
    }

    private mapPackageContents(result: PagingToken<ShippingRequestGridItem[]>): PagingToken<ShippingRequestGridItem[]> {
        if (result.data && result.data.length) {
            result.data.forEach((item) => {
                const contents = [];

                if (item.packageContents?.length) {
                    item.packageContents.forEach((a) => contents.push(`${a.quantity} x ${a.brand} ${a.name}`));
                }

                if (item.otherFitMask) {
                    contents.push(`${item.otherFitMaskQuantity || '1'} x ${item.otherFitMask}`);
                }

                if (item.miscPackageContents?.length) {
                    item.miscPackageContents.forEach((a) => contents.push(`${a.quantity} x ${a.name}`));
                }

                if (item.miscellaneousOther) {
                    contents.push(`1 x ${item.miscellaneousOther}`);
                }

                item.packageContentMapped = contents.join('<br/>').trim();
            });
        }
        return result;
    }
}
